import React, { useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    createFilterOptions,
    FormControl,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import GetData from "../../services/GetData.js";
import PostData from "../../services/PostData.js";

const CREATE_OPTION_ID = "__create_dimension__";
const defaultFilterOptions = createFilterOptions();

const labelSx = {
    fontSize: 12,
    fontWeight: 700,
    color: "#5c6570",
    lineHeight: 1.3,
};

const hintSx = {
    fontSize: 11,
    color: "#94a3b8",
    lineHeight: 1.3,
};

const valueBoxSx = {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    px: 1.25,
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    color: "#1c2430",
    fontSize: 14,
    minWidth: 0,
    overflow: "hidden",
};

/** Label sits above the control so it never shares the input with the value. */
export function FieldShell({ label, hint, action, children, sx }) {
    return (
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5, ...sx }}>
            {label || action ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
                    {label ? (
                        <Typography component="span" sx={labelSx}>
                            {label}
                        </Typography>
                    ) : (
                        <span />
                    )}
                    {action}
                </Box>
            ) : null}
            {children}
            {hint ? (
                <Typography component="span" sx={hintSx}>
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}

/** Text or number stored on this record. The label is not inside the input. */
export function SkuTextField({ label, hint, sx, ...fieldProps }) {
    return (
        <FieldShell label={label} hint={hint} sx={sx}>
            <TextField
                {...fieldProps}
                size="small"
                fullWidth
                hiddenLabel
                placeholder=""
            />
        </FieldShell>
    );
}

/** Single-select stored on this record. Choose a choice, or clear it. */
export function SingleSelectField({
    label,
    value,
    onChange,
    choices = [],
    disabled = false,
    hint,
    onManage,
}) {
    const saved = choices.filter((choice) => choice !== "");
    const options = value && !saved.includes(value) ? [value, ...saved] : saved;
    const action = onManage ? (
        <Button
            size="small"
            onClick={onManage}
            sx={{
                textTransform: "none",
                fontWeight: 700,
                minWidth: 0,
                px: 0.5,
                py: 0,
                color: "#0d9488",
            }}
        >
            Edit options
        </Button>
    ) : null;
    return (
        <FieldShell label={label} hint={hint} action={action}>
            <FormControl size="small" fullWidth disabled={disabled}>
                <Select
                    displayEmpty
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    renderValue={(selected) => selected || "None"}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {options.map((choice) => (
                        <MenuItem key={choice} value={choice}>
                            {choice}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </FieldShell>
    );
}

/** Multiple choices stored on this record. Add or remove them here. */
export function MultiSelectField({
    label,
    value = [],
    onChange,
    choices = [],
    disabled = false,
    hint,
}) {
    const selected = Array.isArray(value) ? value : [];
    return (
        <FieldShell label={label} hint={hint}>
            <FormControl size="small" fullWidth disabled={disabled}>
                <Select
                    multiple
                    displayEmpty
                    value={selected}
                    onChange={(e) => onChange(e.target.value)}
                    renderValue={(vals) => (vals.length ? vals.join(", ") : "None")}
                >
                    {choices.filter(Boolean).map((choice) => (
                        <MenuItem key={choice} value={choice}>
                            <Checkbox checked={selected.includes(choice)} />
                            <ListItemText primary={choice} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </FieldShell>
    );
}

function recordLabel(option) {
    if (option?.__create) {
        return `Create "${option.name}"`;
    }
    return option?.name || option?.code || String(option?._id || "");
}

function mergeOptions(...lists) {
    const byId = new Map();
    for (const list of lists) {
        for (const option of list || []) {
            if (!option?._id) continue;
            byId.set(String(option._id), option);
        }
    }
    return [...byId.values()];
}

function withCreateOption(options, inputValue, allowCreate) {
    const q = String(inputValue || "").trim();
    if (!allowCreate || !q) return options;
    const exists = (options || []).some(
        (option) =>
            !option?.__create &&
            String(option?.name || "").trim().toLowerCase() === q.toLowerCase()
    );
    if (exists) return options;
    return [
        ...(options || []),
        { _id: CREATE_OPTION_ID, name: q, __create: true },
    ];
}

async function createDimensionRecord(endpoint, name) {
    const trimmed = String(name || "").trim();
    if (!endpoint || !trimmed) {
        return { ok: false, message: "Name is required." };
    }
    const result = await PostData({ name: trimmed }, endpoint);
    if (result?.success && result?.data?._id) {
        return { ok: true, record: result.data };
    }
    const message =
        result?.error?.response?.data?.message ||
        result?.error?.message ||
        "Could not create record.";
    return { ok: false, message };
}

/** Debounced server search against a dimension list endpoint (`broylesllc/brands`, etc.). */
function useDimensionSearch(endpoint, enabled = true) {
    const [input, setInput] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [fetched, setFetched] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQ(input.trim()), 250);
        return () => clearTimeout(timer);
    }, [input]);

    useEffect(() => {
        if (!endpoint || !enabled) return undefined;
        // Wait until the menu is open (or user typed) so we don't fetch on every dialog mount.
        if (!open && !debouncedQ) return undefined;

        let cancelled = false;
        (async () => {
            setLoading(true);
            const params = new URLSearchParams({
                limit: "100",
                sort: "name",
                sortDir: "asc",
            });
            if (debouncedQ) params.set("q", debouncedQ);
            const { getData, error } = await GetData(`${endpoint}?${params.toString()}`);
            if (cancelled) return;
            setLoading(false);
            if (error) {
                setFetched([]);
                return;
            }
            setFetched(Array.isArray(getData?.items) ? getData.items : []);
        })();

        return () => {
            cancelled = true;
        };
    }, [endpoint, enabled, open, debouncedQ]);

    return {
        open,
        setOpen,
        input,
        setInput,
        fetched,
        loading,
    };
}

/** One linked record from another table. Clear the field to remove the link. */
export function DimensionSingleSelect({
    label,
    valueId,
    options = [],
    /** When set (e.g. `broylesllc/brands`), search the server instead of filtering a capped local list. */
    endpoint,
    onChange,
    disabled = false,
    hint,
    /** When true (default with endpoint), type a new name and choose Create to save to that table. */
    allowCreate = true,
}) {
    const asyncMode = Boolean(endpoint);
    const canCreate = Boolean(endpoint) && allowCreate;
    const { open, setOpen, input, setInput, fetched, loading } = useDimensionSearch(
        endpoint,
        asyncMode
    );
    const [pinned, setPinned] = useState(null);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");

    useEffect(() => {
        if (!valueId) {
            setPinned(null);
            return;
        }
        const match =
            options.find((option) => String(option._id) === String(valueId)) ||
            fetched.find((option) => String(option._id) === String(valueId));
        if (match) setPinned(match);
    }, [valueId, options, fetched]);

    const merged = useMemo(
        () => mergeOptions(pinned ? [pinned] : [], options, asyncMode ? fetched : []),
        [pinned, options, asyncMode, fetched]
    );
    const selected =
        merged.find((option) => String(option._id) === String(valueId)) || null;

    const handleChange = async (_event, next) => {
        setCreateError("");
        if (!next) {
            setPinned(null);
            onChange("");
            return;
        }
        if (next.__create) {
            setCreating(true);
            const result = await createDimensionRecord(endpoint, next.name);
            setCreating(false);
            if (!result.ok) {
                setCreateError(result.message);
                return;
            }
            setPinned(result.record);
            setInput("");
            onChange(result.record._id);
            return;
        }
        setPinned(next);
        onChange(next._id);
    };

    return (
        <FieldShell
            label={label}
            hint={
                hint ||
                (canCreate
                    ? "Search or type a new name to create"
                    : "Choose a record from this table")
            }
        >
            <Autocomplete
                size="small"
                disabled={disabled || creating}
                open={asyncMode ? open : undefined}
                onOpen={asyncMode ? () => setOpen(true) : undefined}
                onClose={asyncMode ? () => setOpen(false) : undefined}
                options={merged}
                value={selected}
                loading={creating || (asyncMode && loading)}
                filterOptions={(opts, state) => {
                    const base = asyncMode
                        ? opts
                        : defaultFilterOptions(opts, state);
                    return withCreateOption(
                        base,
                        asyncMode ? input : state.inputValue,
                        canCreate
                    );
                }}
                getOptionLabel={recordLabel}
                isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                onChange={handleChange}
                onInputChange={(_event, value, reason) => {
                    if (reason === "input" || reason === "clear") {
                        setCreateError("");
                    }
                    if (!asyncMode) return;
                    if (reason === "input") setInput(value);
                    if (reason === "clear") setInput("");
                }}
                renderOption={(props, option) => (
                    <li {...props} key={String(option._id)}>
                        {option.__create ? (
                            <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
                                Create &ldquo;{option.name}&rdquo;
                            </Typography>
                        ) : (
                            recordLabel(option)
                        )}
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        hiddenLabel
                        placeholder={canCreate ? "Search or create…" : "Search records"}
                        error={Boolean(createError)}
                        helperText={createError || undefined}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {creating || (asyncMode && loading) ? (
                                        <CircularProgress color="inherit" size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </FieldShell>
    );
}

/** Linked records from another table. Add or remove links on this SKU. */
export function DimensionMultiSelect({
    label,
    valueIds = [],
    options = [],
    endpoint,
    onChange,
    disabled = false,
    hint,
    allowCreate = true,
}) {
    const asyncMode = Boolean(endpoint);
    const canCreate = Boolean(endpoint) && allowCreate;
    const { open, setOpen, input, setInput, fetched, loading } = useDimensionSearch(
        endpoint,
        asyncMode
    );
    const [pinned, setPinned] = useState([]);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");

    useEffect(() => {
        const ids = valueIds.map(String);
        if (!ids.length) {
            setPinned([]);
            return;
        }
        setPinned((prev) => {
            const fromLists = mergeOptions(prev, options, fetched).filter((option) =>
                ids.includes(String(option._id))
            );
            return fromLists;
        });
    }, [valueIds, options, fetched]);

    const merged = useMemo(
        () => mergeOptions(pinned, options, asyncMode ? fetched : []),
        [pinned, options, asyncMode, fetched]
    );
    const selected = merged.filter((option) =>
        valueIds.map(String).includes(String(option._id))
    );

    const handleChange = async (_event, next) => {
        setCreateError("");
        const list = Array.isArray(next) ? next : [];
        const createOpt = list.find((option) => option?.__create);
        if (createOpt) {
            setCreating(true);
            const result = await createDimensionRecord(endpoint, createOpt.name);
            setCreating(false);
            if (!result.ok) {
                setCreateError(result.message);
                return;
            }
            const withoutCreate = list.filter((option) => !option?.__create);
            const mergedNext = mergeOptions(withoutCreate, [result.record]);
            setPinned(mergedNext);
            setInput("");
            onChange(mergedNext.map((option) => option._id));
            return;
        }
        setPinned(list);
        onChange(list.map((option) => option._id));
    };

    return (
        <FieldShell
            label={label}
            hint={
                hint ||
                (canCreate
                    ? "Search, link, or type a new name to create"
                    : "Add or remove records from this table")
            }
        >
            <Autocomplete
                multiple
                size="small"
                disabled={disabled || creating}
                open={asyncMode ? open : undefined}
                onOpen={asyncMode ? () => setOpen(true) : undefined}
                onClose={asyncMode ? () => setOpen(false) : undefined}
                options={merged}
                value={selected}
                loading={creating || (asyncMode && loading)}
                filterOptions={(opts, state) => {
                    const base = asyncMode
                        ? opts
                        : defaultFilterOptions(opts, state);
                    return withCreateOption(
                        base,
                        asyncMode ? input : state.inputValue,
                        canCreate
                    );
                }}
                getOptionLabel={recordLabel}
                isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                onChange={handleChange}
                onInputChange={(_event, value, reason) => {
                    if (reason === "input" || reason === "clear") {
                        setCreateError("");
                    }
                    if (!asyncMode) return;
                    if (reason === "input") setInput(value);
                    if (reason === "clear") setInput("");
                }}
                renderOption={(props, option) => (
                    <li {...props} key={String(option._id)}>
                        {option.__create ? (
                            <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
                                Create &ldquo;{option.name}&rdquo;
                            </Typography>
                        ) : (
                            recordLabel(option)
                        )}
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        hiddenLabel
                        placeholder={canCreate ? "Search or create…" : "Search records"}
                        error={Boolean(createError)}
                        helperText={createError || undefined}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {creating || (asyncMode && loading) ? (
                                        <CircularProgress color="inherit" size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </FieldShell>
    );
}

/** Formula, rollup, or lookup. Shown as text, not an editable input. */
export function LookupField({ label, value, source }) {
    const empty = value == null || value === "";
    return (
        <FieldShell label={label} hint={source}>
            <Box sx={{ ...valueBoxSx, color: empty ? "#94a3b8" : "#1c2430" }}>
                <Box
                    component="span"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {empty ? "—" : String(value)}
                </Box>
            </Box>
        </FieldShell>
    );
}
